/**
 * Chronological feed (BRIEF §24): purchases with their round-up outcome,
 * grouped by day, pending items marked.
 */
import { formatPence, formatPenceShort } from "../lib/money";
import type { RoundupInvestment } from "../lib/types";

function dayLabel(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
}

function InvestCell({ r }: { r: RoundupInvestment }) {
  switch (r.status) {
    case "complete":
      return (
        <div className="feed-invest">
          <div className="amount">
            +{formatPence(r.investedPence ?? 0, r.currency)} {r.stockSymbol}
          </div>
          <div className="detail">
            {formatPenceShort(r.roundupPence, r.currency)} round-up
            {(r.executionCostPence ?? 0) > 0 &&
              ` · ${formatPenceShort(r.executionCostPence!, r.currency)} cost`}
          </div>
        </div>
      );
    case "queued":
      return (
        <div className="feed-invest">
          <div className="amount pending">+{formatPenceShort(r.roundupPence, r.currency)} pending</div>
          <div className="detail">{r.statusDetail ?? "Waiting to invest"}</div>
        </div>
      );
    case "skipped":
      return (
        <div className="feed-invest">
          <div className="amount muted">no round-up</div>
          <div className="detail">{r.statusDetail}</div>
        </div>
      );
    case "failed":
      return (
        <div className="feed-invest">
          <div className="amount pending">retry pending</div>
          <div className="detail">{formatPenceShort(r.roundupPence, r.currency)} round-up</div>
        </div>
      );
    default:
      return (
        <div className="feed-invest">
          <div className="amount pending">investing…</div>
          <div className="detail">{formatPenceShort(r.roundupPence, r.currency)} round-up</div>
        </div>
      );
  }
}

export function ActivityFeed({ roundups }: { roundups: RoundupInvestment[] }) {
  if (roundups.length === 0) {
    return (
      <div className="empty-state">
        <div className="icon">🛒</div>
        No purchases yet.
        <br />
        Spend with your card and your round-ups will appear here.
      </div>
    );
  }

  let lastDay = "";
  return (
    <div>
      {roundups.map((r) => {
        const day = dayLabel(r.timestamp);
        const header = day !== lastDay ? <div className="feed-day">{day}</div> : null;
        lastDay = day;
        return (
          <div key={r.id}>
            {header}
            <div className="feed-item">
              <div>
                <div className="feed-merchant">{r.merchant}</div>
                <div className="feed-spent">{formatPence(r.purchasePence, r.currency)} spent</div>
              </div>
              <InvestCell r={r} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
